'use client';

import { useEffect, useState } from 'react';
import Image from 'next/image';
import Icon from '@/components/ui/AppIcon';
import type { ProductImage } from '../productsData';

interface ProductGalleryProps {
  images: ProductImage[];
  /** Fallback alt when an image has none. */
  alt: string;
}

// Product photo gallery used inside the detail modal: a main image with a
// thumbnail strip and prev/next controls. Images are local /assets paths, so
// next/image serves responsive (incl. mobile) variants automatically.
export default function ProductGallery({ images, alt }: ProductGalleryProps) {
  const count = images.length;
  // Resets to the first shot per product: the modal remounts this via `key`.
  const [active, setActive] = useState(0);

  // Arrow-key navigation while the gallery is mounted (modal is open).
  useEffect(() => {
    if (count <= 1) return;
    const onKey = (e: KeyboardEvent) => {
      // The gallery now renders on the full product detail page (not just a
      // modal), so don't hijack arrow keys while the visitor is typing in the
      // inquiry form or any other field.
      const target = e.target as HTMLElement | null;
      if (target?.closest('input, textarea, select, [contenteditable="true"]')) return;
      if (e.key === 'ArrowLeft') setActive((i) => (i - 1 + count) % count);
      else if (e.key === 'ArrowRight') setActive((i) => (i + 1) % count);
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [count]);

  if (count === 0) return null;
  const current = images[Math.min(active, count - 1)];
  const go = (dir: -1 | 1) => setActive((i) => (i + dir + count) % count);

  return (
    <div>
      <div className="relative aspect-[4/3] w-full overflow-hidden bg-muted">
        <Image
          key={current.src}
          src={current.src}
          alt={current.alt || alt}
          fill
          sizes="(min-width: 768px) 768px, 100vw"
          priority
          className="object-cover object-center"
        />

        {count > 1 && (
          <>
            <button
              type="button"
              onClick={() => go(-1)}
              aria-label="Previous image"
              className="absolute left-3 top-1/2 grid h-11 w-11 -translate-y-1/2 place-items-center rounded-full bg-background/80 text-foreground shadow-sm backdrop-blur transition hover:bg-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary"
            >
              <Icon name="ChevronRightIcon" size={20} className="rotate-180" />
            </button>
            <button
              type="button"
              onClick={() => go(1)}
              aria-label="Next image"
              className="absolute right-3 top-1/2 grid h-11 w-11 -translate-y-1/2 place-items-center rounded-full bg-background/80 text-foreground shadow-sm backdrop-blur transition hover:bg-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary"
            >
              <Icon name="ChevronRightIcon" size={20} />
            </button>
            <span className="absolute bottom-3 right-3 rounded-full bg-foreground/70 px-2.5 py-1 font-cta text-xs text-background">
              {active + 1} / {count}
            </span>
          </>
        )}
      </div>

      {count > 1 && (
        <div className="flex gap-2 overflow-x-auto px-1 py-3">
          {images.map((img, i) => (
            <button
              key={img.src}
              type="button"
              onClick={() => setActive(i)}
              aria-label={`View image ${i + 1}`}
              aria-current={i === active}
              className={`relative h-16 w-16 shrink-0 overflow-hidden rounded-lg border-2 transition ${
                i === active ? 'border-primary' : 'border-transparent opacity-70 hover:opacity-100'
              }`}
            >
              {/* Descriptive alt for image search (audit 4.3); the button's
                  aria-label remains the accessible name, so screen readers
                  aren't double-announced. */}
              <Image
                src={img.src}
                alt={img.alt || `${alt} - view ${i + 1}`}
                fill
                sizes="64px"
                className="object-cover"
              />
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
